import { HKPImage } from "../../../media/HKPImage";
import React,{useEffect, useMemo, useRef, useState, type KeyboardEvent} from 'react'
import { Card, Row, Col } from 'reactstrap';
import { Grid, Button, Divider, InputBase } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { isArray } from 'lodash';
import notifications from "../../../common/Components/Notifications/Notification";
import { CatalogueCommerceContext } from "./catalogueCommerceClient";
import {
  upsertVisitorShelfCartLine,
  upsertVisitorShelfShortlist,
} from "./catalogueVisitorClient";
import { useCatalogueVisitor } from "./CatalogueVisitorContext";
import { isSharedCatalogueSold } from "../../products/productStockDisplay";
import {
  isPreviewVideo,
  pickProductListThumbUrl,
  videoSrcWithPreviewFrame,
} from "../../products/previewMedia";
import { catalogueBuyerPriceCopy, isPublicSellPriceMissing } from "./publicSellPrice";
import ClientPieceActions from "./ClientPieceActions";
import { gridImageUrl, sharedPieceUrl } from "./publicCatalogueHelpers";
import { useAskOnWhatsApp } from "./useAskOnWhatsApp";
import PieceStory from "./PieceStory";
import { recordStoryOpen } from "../../../components/catalogue/catalogueResellerApi";
import SwipeGallery from "./SwipeGallery";
import { savePhotoUrl } from "./savePhoto";

type CatalogueProductInfoProps = {
  form: any;
  catalogueContext?: CatalogueCommerceContext;
};

function pickListingImage(form: any): string {
  if (!form?.profileURL || !isArray(form.profileURL)) return "";
  return pickProductListThumbUrl(form.profileURL);
}

function VideoPlayMark() {
  return (
    <span className="hkp-media-play" aria-hidden="true">
      <span className="hkp-media-play__triangle" />
    </span>
  );
}

function catalogueFamilyLabel(item: any, currentColor?: string) {
  const color = item?.color || "no colour";
  const sn = item?.orgSrNo != null ? `SN ${item.orgSrNo}` : "";
  const tag = item?.srNo ? `tag ${item.srNo}` : "";
  const sameColor = (item?.color || "") === (currentColor || "");
  if (sameColor) {
    return [color, sn, tag].filter(Boolean).join(" · ");
  }
  return [color, sn].filter(Boolean).join(" · ");
}

function CatalogueProductInfo(props: CatalogueProductInfoProps) {
  const { form, catalogueContext } = props;
  const history = useHistory();
  const location = useLocation();
  const { isVisitorSession, openAuthModal, ensureVisitorForCommerce } = useCatalogueVisitor();
  const [cartQty, setCartQty] = useState(1);
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const [cartBusy, setCartBusy] = useState(false);
  const [priceRequestOpen, setPriceRequestOpen] = useState(false);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);
    const [toggleProductDetails, setToggleProductDetails]: any = useState({
        productSpecs: true,
        description: false,
        moreInfo: false
      })
    const visibleMedia = useMemo(() => {
      if (!isArray(form?.profileURL)) return [];
      return form.profileURL.filter((ele: any) => ele?.show && ele?.blobImage);
    }, [form?.profileURL]);
    useEffect(() => {
      setPriceRequestOpen(false);
    }, [form?.productUniqueId]);
    useEffect(() => {
      if (activeMediaIndex > visibleMedia.length - 1) {
        setActiveMediaIndex(0);
      }
    }, [visibleMedia.length, activeMediaIndex]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    // Phones get a swipe gallery on shared catalogue pages (layout decision, not device sniffing).
    const [isPhone, setIsPhone] = useState(
      () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches
    );
    useEffect(() => {
      const mq = window.matchMedia("(max-width: 720px)");
      const apply = () => setIsPhone(mq.matches);
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }, []);
    const [zoomStep, setZoomStep] = useState(0);
    const activeMedia = visibleMedia[activeMediaIndex] || null;
    const isVideo = (item: any) => isPreviewVideo(item);
    const hasCarousel = visibleMedia.length > 1;
    // A video shows its own first frame, never a photo standing in for it; only the one on screen loads that frame.
    const viewerRef = useRef<HTMLDivElement>(null);
    // A video never keeps playing once the buyer moves to another photo.
    useEffect(() => {
      viewerRef.current?.querySelectorAll("video").forEach((video) => {
        if (!video.closest(".is-active-media")) video.pause();
      });
    }, [activeMediaIndex]);
    // Thumbnails take the 320 copy; a share link's marked photo has no copy that small, so it takes the grid one.
    const thumbUrl = (url: string) => (url?.includes("/catalogue-media/public/") ? gridImageUrl(url) || url : url);

    const goToMedia = (index: number) => {
      if (visibleMedia.length === 0) return;
      if (index < 0) {
        setActiveMediaIndex(visibleMedia.length - 1);
        return;
      }
      if (index >= visibleMedia.length) {
        setActiveMediaIndex(0);
        return;
      }
      setActiveMediaIndex(index);
    };

    const closeLightbox = () => {
      setLightboxOpen(false);
      setZoomStep(0);
    };

    const openLightbox = () => {
      setZoomStep(0);
      setLightboxOpen(true);
    };

    const cycleZoom = () => {
      setZoomStep((step) => (step >= 2 ? 0 : step + 1));
    };

    useEffect(() => {
      setLightboxOpen(false);
      setZoomStep(0);
    }, [form?.productUniqueId]);

    useEffect(() => {
      setZoomStep(0);
      if (activeMedia && isVideo(activeMedia)) {
        setLightboxOpen(false);
      }
    }, [activeMediaIndex, activeMedia]);

    useEffect(() => {
      if (!lightboxOpen) return;
      const onKey = (event: globalThis.KeyboardEvent) => {
        if (event.key === "Escape") {
          closeLightbox();
          return;
        }
        if (event.key === "ArrowLeft" && hasCarousel) {
          goToMedia(activeMediaIndex - 1);
        }
        if (event.key === "ArrowRight" && hasCarousel) {
          goToMedia(activeMediaIndex + 1);
        }
      };
      window.addEventListener("keydown", onKey);
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = previousOverflow;
      };
    }, [lightboxOpen, hasCarousel, activeMediaIndex, visibleMedia.length]);

    /** Public catalogue: productUniqueId only — never Mongo _id. */
    const uniqueId =
      form?.productUniqueId != null ? String(form.productUniqueId).trim() : "";
    const routeId =
      catalogueContext?.routeProductId != null
        ? String(catalogueContext.routeProductId).trim()
        : "";
    const mongoId = form?._id != null ? String(form._id).trim() : "";
    const stableProductKey = catalogueContext
      ? uniqueId || routeId
      : mongoId || uniqueId || routeId;

    const isClient = catalogueContext?.audience === "client";
    // Buyers ask the studio with a short link to this piece; its WhatsApp preview shows the photo.
    const pageId = routeId || stableProductKey;
    const studioAsk = useAskOnWhatsApp({
      phone: catalogueContext?.studioWhatsApp,
      pieceName: String(form?.productName || "this piece"),
      ctx: {
        catalogueId: catalogueContext?.catalogueId,
        linkToken: catalogueContext?.linkToken,
        sessionToken: catalogueContext?.sessionToken,
        pieceId: pageId,
      },
      fallback: {
        pieceUrl: typeof window !== "undefined" ? sharedPieceUrl(window.location.href, pageId) : undefined,
        photoUrl: pickListingImage(form),
      },
      eager: Boolean(catalogueContext?.studioWhatsApp) && !isClient,
    });
    const storyAttribution = (() => {
      if (isClient) return "Making story recorded on HKP.";
      const by = form?.productOwner || form?.org || form?.nameOfBrand;
      return by
        ? `Shared by ${by}, as they understand this piece.`
        : "Shared by the maker, as they understand this piece.";
    })();
    // A reseller's client has no studio cart; they like pieces and ask the reseller instead.
    const showCommerce = Boolean(catalogueContext?.catalogueId && stableProductKey) && !isClient;
    const sold = isSharedCatalogueSold(form);
    const buyerPrice = catalogueBuyerPriceCopy(form?.sellPrice, form?.gstRate);
    const clampQty = (n: number) => Math.min(999, Math.max(1, Math.trunc(n)));
    const qtySafe = clampQty(Number(cartQty) || 1);

    const onQtyKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setCartQty((q) => clampQty((Number(q) || 1) + 1));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setCartQty((q) => clampQty((Number(q) || 1) - 1));
      }
    };

    const onShortlist = async () => {
      if (!catalogueContext?.catalogueId || !stableProductKey || wishlistBusy) return;
      const ready =
        isVisitorSession ||
        (await ensureVisitorForCommerce({
          sessionToken: catalogueContext.sessionToken,
        }));
      if (!ready) {
        openAuthModal();
        return;
      }
      setWishlistBusy(true);
      try {
        const shelfRes = await upsertVisitorShelfShortlist(catalogueContext.catalogueId, {
          productKey: String(stableProductKey),
          productId: String(stableProductKey),
          productUniqueId:
            form?.productUniqueId != null ? String(form.productUniqueId) : undefined,
          productName: form?.productName || "Product",
          productColor: form?.color,
          productImage: pickListingImage(form) || undefined,
          designNum: form?.designNum,
          shareLinkToken: catalogueContext?.linkToken || undefined,
        });
        if (!shelfRes?.success) {
          notifications({
            title: "Shortlist",
            message: shelfRes?.message || "Could not save to your account.",
            type: "danger",
          });
          return;
        }
        notifications({
          title: "Shortlist",
          message:
            "Saved as a draft in My selections. Submit from there when you want the studio to receive it.",
          type: "success",
        });
      } catch {
        notifications({
          title: "Shortlist",
          message: "Network error. Try again.",
          type: "danger",
        });
      } finally {
        setWishlistBusy(false);
      }
    };

    const onAddToCart = async () => {
      if (!catalogueContext?.catalogueId || !stableProductKey || cartBusy) return;
      const ready =
        isVisitorSession ||
        (await ensureVisitorForCommerce({
          sessionToken: catalogueContext.sessionToken,
        }));
      if (!ready) {
        openAuthModal();
        return;
      }
      setCartBusy(true);
      try {
        const shelfRes = await upsertVisitorShelfCartLine(catalogueContext.catalogueId, {
          productKey: String(stableProductKey),
          productId: String(stableProductKey),
          productUniqueId:
            form?.productUniqueId != null ? String(form.productUniqueId) : undefined,
          productName: form?.productName || "Product",
          productColor: form?.color,
          productImage: pickListingImage(form) || undefined,
          designNum: form?.designNum,
          quantity: qtySafe,
          sellPriceMrpSnapshot:
            form?.sellPrice != null && form?.sellPrice !== ""
              ? String(form.sellPrice)
              : undefined,
          shareLinkToken: catalogueContext?.linkToken || undefined,
        });
        if (!shelfRes?.success) {
          notifications({
            title: "Cart",
            message: shelfRes?.message || "Could not save to your account.",
            type: "danger",
          });
          return;
        }
        notifications({
          title: "Cart",
          message: `Added ${qtySafe} to your draft cart in My selections. Submit from there to send it to the studio.`,
          type: "success",
        });
      } catch {
        notifications({
          title: "Cart",
          message: "Network error. Try again.",
          type: "danger",
        });
      } finally {
        setCartBusy(false);
      }
    };

    const btnPrimarySx = {
      fontFamily: '"Hanken Grotesk", -apple-system, system-ui, sans-serif',
      bgcolor: "#0071E3",
      color: "#fff",
      fontWeight: 650,
      letterSpacing: "-0.01em",
      textTransform: "none" as const,
      borderRadius: "100px",
      px: 3,
      py: 1.5,
      boxShadow: "0 10px 24px -10px rgba(0, 113, 227, 0.55)",
      "&:hover": { bgcolor: "#0058C2" },
      "&:disabled": { bgcolor: "#A8C7F0", color: "#fff", boxShadow: "none" },
    };
    const btnOutlinedSx = {
      fontFamily: '"Hanken Grotesk", -apple-system, system-ui, sans-serif',
      borderColor: "#D9D9E0",
      color: "#0071E3",
      fontWeight: 650,
      letterSpacing: "-0.01em",
      textTransform: "none" as const,
      borderRadius: "100px",
      borderWidth: "1px",
      px: 3,
      py: 1.5,
      "&:hover": {
        borderColor: "#86868B",
        borderWidth: "1px",
        bgcolor: "#fff",
      },
      "&:disabled": { borderColor: "#E8E8ED", color: "#86868B" },
    };

    return (
        <div className='productinfoDiv'>
        <Card className='product-image-card'>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={7} md={8}>
              <div className="hkp-media-viewer" ref={viewerRef}>
                {activeMedia && catalogueContext && isPhone ? (
                  <SwipeGallery
                    items={visibleMedia}
                    index={activeMediaIndex}
                    onIndexChange={setActiveMediaIndex}
                    label={`${form?.productName || "Piece"} photos`}
                    renderSlide={(item: any, i: number) =>
                      isVideo(item) ? (
                        <div className={i === activeMediaIndex ? "is-active-media" : undefined}>
                          <video
                            controls
                            preload={i === activeMediaIndex ? "metadata" : "none"}
                            playsInline
                            src={videoSrcWithPreviewFrame(item?.blobImage)}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveMediaIndex(i);
                            openLightbox();
                          }}
                          aria-label="Enlarge photo"
                        >
                          <HKPImage
                            preset="PRODUCT_DETAIL"
                            legacyUrl={item?.blobImage}
                            alt={`${form?.productName || "Piece"} photo ${i + 1}`}
                          />
                        </button>
                      )
                    }
                  />
                ) : activeMedia ? (
                  <>
                    <div className="hkp-media-stage">
                      {isVideo(activeMedia) ? (
                        <div className="hkp-media-stage-video is-active-media">
                          <video
                            key={`video-${activeMediaIndex}`}
                            className="product-img"
                            controls
                            preload="metadata"
                            playsInline
                            src={videoSrcWithPreviewFrame(activeMedia?.blobImage)}
                          />
                          <span className="hkp-media-video-badge">Video</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="hkp-media-open-zoom"
                          onClick={openLightbox}
                          aria-label="Enlarge product image"
                        >
                          <HKPImage preset="PRODUCT_DETAIL"
                            key={`image-${activeMediaIndex}`}
                            className="product-img"
                            legacyUrl={activeMedia?.blobImage}
                            alt={form?.productName || "product media"}
                          />
                        </button>
                      )}
                      {hasCarousel && (
                        <>
                          <button
                            type="button"
                            className="hkp-media-nav hkp-media-nav-prev"
                            onClick={() => goToMedia(activeMediaIndex - 1)}
                            aria-label="Previous media"
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            className="hkp-media-nav hkp-media-nav-next"
                            onClick={() => goToMedia(activeMediaIndex + 1)}
                            aria-label="Next media"
                          >
                            ›
                          </button>
                        </>
                      )}
                    </div>
                    {!isVideo(activeMedia) ? (
                      <p className="hkp-media-zoom-hint">Click image to enlarge</p>
                    ) : null}
                    {hasCarousel && (
                      <div className="hkp-media-thumbs">
                        {visibleMedia.map((ele: any, i: number) => (
                          <button
                            key={`${ele?.blobImage}-${i}`}
                            type="button"
                            className={`hkp-media-thumb ${i === activeMediaIndex ? "active" : ""}`}
                            onClick={() => goToMedia(i)}
                            aria-label={isVideo(ele) ? `Open video ${i + 1}` : `Open media ${i + 1}`}
                          >
                            {isVideo(ele) ? (
                              <span className="hkp-media-thumb-video">
                                <video
                                  src={videoSrcWithPreviewFrame(ele?.blobImage)}
                                  muted
                                  preload="none"
                                  playsInline
                                />
                                <VideoPlayMark />
                              </span>
                            ) : (
                              <HKPImage preset="THUMBNAIL" legacyUrl={thumbUrl(ele?.blobImage)} alt={`thumbnail ${i + 1}`} />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="hkp-media-empty">No media available for this product.</div>
                )}
                {catalogueContext?.allowDownload && activeMedia && !isVideo(activeMedia) && savePhotoUrl(activeMedia?.blobImage) ? (
                  <a className="hkp-save-photo" href={savePhotoUrl(activeMedia?.blobImage)!} download>
                    Save photo
                  </a>
                ) : null}
                {lightboxOpen && activeMedia && !isVideo(activeMedia) ? (
                  <div
                    className="hkp-media-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enlarged product image"
                    onClick={closeLightbox}
                  >
                    <button
                      type="button"
                      className="hkp-media-lightbox-close"
                      onClick={closeLightbox}
                      aria-label="Close enlarged image"
                    >
                      Close
                    </button>
                    {hasCarousel ? (
                      <>
                        <button
                          type="button"
                          className="hkp-media-lightbox-nav hkp-media-lightbox-prev"
                          onClick={(event) => {
                            event.stopPropagation();
                            goToMedia(activeMediaIndex - 1);
                          }}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button
                          type="button"
                          className="hkp-media-lightbox-nav hkp-media-lightbox-next"
                          onClick={(event) => {
                            event.stopPropagation();
                            goToMedia(activeMediaIndex + 1);
                          }}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                      </>
                    ) : null}
                    <div
                      className={`hkp-media-lightbox-frame zoom-${zoomStep}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        cycleZoom();
                      }}
                    >
                      <HKPImage preset="PRODUCT_DETAIL"
                        legacyUrl={activeMedia?.blobImage}
                        alt={form?.productName || "enlarged product media"}
                      />
                    </div>
                    <p className="hkp-media-lightbox-hint">
                      Click image to zoom · Esc to close
                      {hasCarousel ? ` · ${activeMediaIndex + 1} of ${visibleMedia.length}` : ""}
                    </p>
                  </div>
                ) : null}
              </div>

            </Grid>
            <Grid item xs={12} sm={5} md={4} className="hkp-product-buy-col">
              <div className="hkp-product-buy-panel">
              <div className="hkp-product-buy-copy">
              <h1 className="hkp-product-title">{form?.productName || "Product"}</h1>
              {Array.isArray(form?.family) && form.family.length > 1 ? (
                <div className="hkp-product-family">
                  <label htmlFor="hkp-catalogue-family">Color / other piece</label>
                  <select
                    id="hkp-catalogue-family"
                    value={form?.productUniqueId || ""}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      if (!nextId || nextId === form?.productUniqueId) {
                        return;
                      }
                      history.push(
                        `/catalogue-product-info/${encodeURIComponent(nextId)}${location.search || ""}`
                      );
                    }}
                  >
                    {form.family.map((item: any) => (
                      <option key={item.productUniqueId} value={item.productUniqueId}>
                        {catalogueFamilyLabel(item, form?.color)}
                        {item.productUniqueId === form?.productUniqueId ? " (current)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              {isClient || (catalogueContext && !form?.SrNo) ? null : (
                <p className="hkp-product-tag">HKP Tag : {form?.SrNo || "N/A"}</p>
              )}
              {isPublicSellPriceMissing(form?.sellPrice) && isClient ? (
                <div className="hkp-product-price-block">
                  <p className="hkp-product-price">Ask for price</p>
                </div>
              ) : isPublicSellPriceMissing(form?.sellPrice) ? (
                <div className="hkp-product-price-block">
                  {priceRequestOpen ? (
                    <p className="hkp-product-price">Request for price</p>
                  ) : (
                    <button
                      type="button"
                      className="hkp-product-request-price"
                      onClick={() => setPriceRequestOpen(true)}
                    >
                      Request for price
                    </button>
                  )}
                  {priceRequestOpen ? (
                    <p className="hkp-product-price-pending" role="status">
                      You will see the price once the studio reverts on this.
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="hkp-product-price-block">
                  <p className="hkp-product-price">
                    {buyerPrice?.amount} {buyerPrice?.label}
                  </p>
                  <p className="hkp-product-price-gst">{buyerPrice?.detail}</p>
                </div>
              )}
              {sold ? <p className="hkp-product-sold-note">Sold</p> : null}
              {isClient && form?.productUniqueId ? (
                <ClientPieceActions
                  variant="detail"
                  pieceId={String(form.productUniqueId)}
                  pieceName={String(form?.productName || "this piece")}
                  liked={Boolean(catalogueContext?.liked)}
                  onToggleLike={() => catalogueContext?.onToggleLike?.()}
                  whatsApp={catalogueContext?.curatorWhatsApp}
                  curatorName={catalogueContext?.curatorName}
                  catalogueId={catalogueContext?.catalogueId}
                  linkToken={catalogueContext?.linkToken}
                  sessionToken={catalogueContext?.sessionToken}
                />
              ) : null}
              <p className="hkp-product-short-note">
                Authentic handloom product backed by HKP traceability.
              </p>
              </div>
              {showCommerce ? (
                <>
                  <div className="hkp-product-commerce-spacer" aria-hidden />
                  <div className="hkp-product-commerce">
                    <Divider className="hkp-product-commerce-divider" />
                    <p className="hkp-product-commerce-eyebrow">For this catalogue</p>
                    <div className="hkp-product-commerce-body">
                      <div className="hkp-product-commerce-qty">
                        <InputBase
                          className="hkp-product-qty-input"
                          type="number"
                          placeholder="1"
                          inputProps={{
                            min: 1,
                            max: 999,
                            step: 1,
                            "aria-label":
                              "Quantity (minimum 1; use up and down arrow keys to change)",
                          }}
                          value={cartQty}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "") {
                              setCartQty(1);
                              return;
                            }
                            const n = parseInt(raw, 10);
                            if (!Number.isFinite(n)) return;
                            setCartQty(clampQty(n));
                          }}
                          onBlur={() => setCartQty((q) => clampQty(Number(q) || 1))}
                          onKeyDown={onQtyKeyDown}
                          sx={{
                            width: "3.35rem",
                            border: "1px solid #D9D9E0",
                            borderRadius: "12px",
                            bgcolor: "#fff",
                            transition: "border-color 0.15s ease",
                            "&:focus-within": {
                              borderColor: "#0071E3",
                            },
                            "& input": {
                              textAlign: "center",
                              padding: "6px 4px",
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: "#1D1D1F",
                              MozAppearance: "textfield",
                              "&::placeholder": {
                                color: "#a8a29a",
                                opacity: 1,
                                fontWeight: 500,
                              },
                              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                                {
                                  WebkitAppearance: "none",
                                  margin: 0,
                                },
                            },
                          }}
                        />
                      </div>
                      <div className="hkp-product-commerce-actions">
                        <Button
                          variant="outlined"
                          size="medium"
                          className="hkp-product-btn-secondary"
                          disabled={wishlistBusy}
                          onClick={onShortlist}
                          sx={btnOutlinedSx}
                        >
                          {wishlistBusy ? "Saving…" : "Save to shortlist"}
                        </Button>
                        <Button
                          variant="contained"
                          size="medium"
                          disableElevation
                          className="hkp-product-btn-primary"
                          disabled={cartBusy}
                          onClick={onAddToCart}
                          sx={btnPrimarySx}
                        >
                          {cartBusy ? "Adding…" : "Add to cart"}
                        </Button>
                      </div>
                    </div>
                    {catalogueContext?.studioWhatsApp ? (
                      <a
                        className="hkp-studio-ask"
                        href={studioAsk.href}
                        onClick={studioAsk.onClick}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ask the studio on WhatsApp
                      </a>
                    ) : null}
                  </div>
                </>
              ) : null}
              </div>
            </Grid>
          </Grid>
        </Card>
        {catalogueContext ? (
          <PieceStory
            form={form}
            attribution={storyAttribution}
            hideBrand={isClient}
            onOpen={() => {
              if (catalogueContext?.catalogueId && catalogueContext.linkToken && form?.productUniqueId) {
                void recordStoryOpen(
                  catalogueContext.catalogueId,
                  catalogueContext.linkToken,
                  catalogueContext.sessionToken,
                  String(form.productUniqueId)
                );
              }
            }}
          />
        ) : (
        <Card className='product-info-card'>
          <h3 className='p-4'>Product Details</h3>
          <Grid
            container spacing={1}>
            <Grid item xs={12} md={2} >
              <div className="product-c">
                <p className='cursor-pointer'
                  onClick={() => setToggleProductDetails({
                    productSpecs: true
                  })}
                >PRODUCT SPECS</p>
                {
                  toggleProductDetails?.productSpecs &&
                  <hr className='p-hr'></hr>
                }
                <p className='cursor-pointer' onClick={() => setToggleProductDetails({
                  description: true
                })}>DESCRIPTION</p>
                {
                  toggleProductDetails?.description &&
                  <hr className='p-hr'></hr>
                }
                <p className='cursor-pointer' onClick={() => setToggleProductDetails({
                  moreInfo: true
                })}>MORE INFO</p>
                {
                  toggleProductDetails?.moreInfo &&
                  <hr className='p-hr'></hr>
                }
              </div>
              <div className='product-c-mobile'>
                <div>
                  <p className={toggleProductDetails?.productSpecs ? "p-moreInfo cursor-pointer" : 'cursor-pointer'}
                    onClick={() => setToggleProductDetails({
                      productSpecs: true
                    })}
                  >PRODUCT SPECS</p>

                </div>
                <div>
                  <p className={toggleProductDetails?.description ? "p-moreInfo cursor-pointer" : 'cursor-pointer'} onClick={() => setToggleProductDetails({
                    description: true
                  })}>DESCRIPTION</p>

                </div>
                <div>
                  <p className={toggleProductDetails?.moreInfo ? "p-moreInfo cursor-pointer" : 'cursor-pointer'} onClick={() => setToggleProductDetails({
                    moreInfo: true
                  })}>MORE INFO</p>
                  {/* {
              toggleProductDetails?.moreInfo &&
              <hr className='p-hr'></hr>
            } */}
                </div>

              </div>

            </Grid>
            <Grid item xs={12} md={10}>
              {
                toggleProductDetails?.productSpecs &&
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6} className='product-grid'>
                      <Row className='first-row'>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Artform</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.artform}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Type</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.type}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Category</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.category}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Sub-Category</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.subCategory}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Weave Type</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.weaveType}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Color of product</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.color}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Types of sarees by design</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.typeOfSareeByDesign}
                        </Col>
                      </Row>

                    </Grid>
                    <Grid item xs={12} md={6} className='product-grid'>
                      <Row className='first-row'>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Weaving Center</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.weavingCenter}
                        </Col>
                      </Row>
                      {isClient ? null : (
                        <Row>
                          <Col xs={6} md={5}>
                            <p className='product-p'>Name of Brand</p>
                          </Col>
                          <Col xs={6} md={7} className='product-colR'>
                            {form?.nameOfBrand}
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Zari Used</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.zariUsed?.label}
                        </Col>
                      </Row> <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Weight of Saree(in Grams)</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.weightOfSaree}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Weight of Zari used</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.weightOfZariUsed}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Purity of Zari</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.purity}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6} md={5}>
                          <p className='product-p'>Yarn type</p>
                        </Col>
                        <Col xs={6} md={7} className='product-colR'>
                          {form?.yarnType}
                        </Col>
                      </Row>
                    </Grid>
                  </Grid>
                </>
              }
              {
                toggleProductDetails?.description && (
                  <Card className="description-card p-3">
                    <p className="text-muted small mb-3" style={{ fontSize: "0.95rem" }}>
                      {(() => {
                        if (isClient) {
                          return "Making story recorded on HKP.";
                        }
                        const by =
                          form?.productOwner || form?.org || form?.nameOfBrand;
                        if (!by) {
                          return "The making story below is shared by the product owner as they understand this piece.";
                        }
                        return `This making story is shared by ${by} as they understand this piece.`;
                      })()}
                    </p>
                    {Array.isArray(form?.processSteps) &&
                    form.processSteps.some((s: any) => s?.title || s?.text) ? (
                      <ol className="ps-3 mb-0">
                        {form.processSteps
                          .filter((s: any) => s?.title || s?.text)
                          .map((s: any, i: number) => (
                            <li key={i} className="mb-3">
                              <strong>{s.title || "Step"}</strong>
                              {s.text ? (
                                <p
                                  className="mb-0 mt-1 text-muted"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {s.text}
                                </p>
                              ) : null}
                            </li>
                          ))}
                      </ol>
                    ) : (
                      <p className="text-muted mb-0">
                        No process steps have been added for this product yet.
                      </p>
                    )}
                    {Array.isArray(form?.flexibleUploadProcesses) &&
                    form.flexibleUploadProcesses.some(
                      (p: any) =>
                        p?.title ||
                        p?.text ||
                        p?.karigar?.name ||
                        (p?.media && p.media.some((m: any) => m?.mediaURL))
                    ) ? (
                      <div className="mt-4 pt-3 border-top">
                        <h6 className="mb-3" style={{ color: "#1D1D1F" }}>
                          Process &amp; media
                        </h6>
                        {form.flexibleUploadProcesses.map((proc: any, idx: number) => {
                          const visibleMedia = (proc?.media || []).filter(
                            (m: any) =>
                              m?.mediaURL &&
                              String(m.mediaURL).trim() !== "" &&
                              m?.show !== "0" &&
                              m?.show !== 0
                          );
                          if (
                            !proc?.title &&
                            !proc?.subtitle &&
                            !proc?.text &&
                            !proc?.karigar?.name &&
                            visibleMedia.length === 0
                          ) {
                            return null;
                          }
                          return (
                            <div key={idx} className="mb-4">
                              {proc?.title ? (
                                <strong className="d-block">{proc.title}</strong>
                              ) : null}
                              {proc?.subtitle ? (
                                <p className="text-muted small mb-1">{proc.subtitle}</p>
                              ) : null}
                              {proc?.text ? (
                                <p
                                  className="text-muted small mb-2"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {proc.text}
                                </p>
                              ) : null}
                              {proc?.karigar?.name ? (
                                <p className="small text-muted mb-2">
                                  Artisan: {proc.karigar.name}
                                </p>
                              ) : null}
                              {visibleMedia.length > 0 ? (
                                <div className="d-flex flex-wrap gap-2">
                                  {visibleMedia.map((m: any, mi: number) => (
                                    <a
                                      key={mi}
                                      href={m.mediaURL}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-inline-block"
                                    >
                                      <HKPImage preset="PRODUCT_DETAIL"
                                        legacyUrl={m.mediaURL}
                                        alt=""
                                        style={{
                                          maxWidth: 120,
                                          maxHeight: 120,
                                          objectFit: "cover",
                                          borderRadius: 8,
                                        }}
                                      />
                                    </a>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </Card>
                )
              }
              {
                toggleProductDetails?.moreInfo && <Card className="moreInfo-card">
                  <Row>
                    <Col xs={12} md={7} className='mt-4'>
                      <Row>
                        <Col xs={6}>
                          Country of Origin
                        </Col>
                        <Col xs={6}>
                          {form?.madeInIndia ? "India" : "N/A"}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          Handloom Mark
                        </Col>
                        <Col xs={6}>
                          {form?.handloom ? "Yes" : "No"}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          Silk Mark
                        </Col>
                        <Col xs={6}>
                          {form?.silkMark ? "Yes" : "No"}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          Loom Number
                        </Col>
                        <Col xs={6}>
                          {form?.loomNum}
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={6}>
                          Design Number
                        </Col>
                        <Col xs={6}>
                          {form?.designNum}
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={12} md={5}>

                    </Col>
                  </Row>

                </Card>
              }
            </Grid>



          </Grid>

        </Card>
        )}
      </div> 
    )
}

export default CatalogueProductInfo

